import { useState, useEffect } from 'react'
import { Menu, Clock } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { useAppStore } from '@/store/appStore'
import { Button } from '@/components/ui/button'
import { sessionsService } from '@/services/sessionsService'
import type { Session } from '@/types'

interface ChatHistoryProps {
    onSelectSession?: (sessionId: string) => void
    currentSessionId?: string
}

export function ChatHistory({ onSelectSession, currentSessionId }: ChatHistoryProps) {
    const auth = useAuth()
    const { sidebarOpen, toggleSidebar, setSidebarOpen } = useAppStore()
    const location = useLocation()
    const [sessions, setSessions] = useState<Session[]>([])
    const [, setIsLoading] = useState(true)

    // Handle responsive sidebar behavior
    useEffect(() => {
        const handleResize = () => {
            const isLargeScreen = window.innerWidth >= 1024 // lg breakpoint
            setSidebarOpen(isLargeScreen)
        }

        // Set initial state based on screen size
        handleResize()

        // Add resize listener
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [setSidebarOpen])

    // Load chat sessions when on chat page
    useEffect(() => {
        if (auth.user && location.pathname === '/') {
            loadChatSessions()
        }
    }, [auth.user, location.pathname])

    const loadChatSessions = async () => {
        try {
            setIsLoading(true)
            const token = auth.user?.access_token
            const sessions = await sessionsService.getSessions(auth.user?.profile?.sub, token)
            setSessions(sessions)
        } catch (error) {
            console.error('Failed to load chat sessions:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffSec = Math.floor(diffMs / 1000)
        if (diffSec < 60) {
            return `${diffSec}s ago`
        }
        const diffMin = Math.floor(diffSec / 60)
        if (diffMin < 60) {
            return `${diffMin}m ago`
        }
        const diffHours = Math.floor(diffMin / 60)
        if (diffHours < 24) {
            return `${diffHours}h ago`
        }
        const diffDays = Math.floor(diffHours / 24)
        return `${diffDays}d ago`
    }

    const handleSessionSelect = (sessionId: string) => {
        if (onSelectSession) {
            onSelectSession(sessionId)
        }
    }

    return (
        <>
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 lg:hidden" onClick={toggleSidebar}>
                    <div className="fixed inset-0 bg-black opacity-25" />
                </div>
            )}

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-80 bg-card border-r border-border shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex h-full flex-col">
                    {/* Recent Sessions */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-sm font-medium text-muted-foreground mb-4">Recent Sessions</h3>
                            <div className="space-y-1">
                                {sessions.map((session) => (
                                    <button
                                        key={session.id}
                                        onClick={() => handleSessionSelect(session.id)}
                                        className={`w-full text-left p-2 rounded-lg transition-colors ${currentSessionId === session.id
                                            ? 'bg-primary/5 text-primary'
                                            : 'hover:bg-accent'
                                            }`} >
                                        <div className="flex items-start gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm truncate">
                                                    {session.title}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Clock size={12} className="text-muted-foreground" />
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatTimeAgo(session.createdAt)}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        • {session.messageCount} messages
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* User info and logout */}
                    <div className="p-4 border-t border-border h-[76px] flex items-center">
                        <div className="flex items-center justify-between gap-2 w-full">
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground truncate">
                                    {auth?.user?.profile?.email || auth?.user?.profile?.sub}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export function MobileMenuButton() {
    const { toggleSidebar } = useAppStore()
    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="lg:hidden"
        >
            <Menu className="h-6 w-6" />
        </Button>
    )
}